import type { Messages } from '../i18n/messages';

/**
 * Home module message catalog (Task 7b of the Fun Mode string sweep). Every
 * static chrome string in packages/app/src/modules/home/* is keyed here.
 * Server/dynamic data (PR/issue titles, repo names, usernames, counts, Jira
 * summaries, entity refs) is never keyed — only interpolated via `{param}`.
 * "Merge Lord" and other brand terms (Jira, GitHub, Sentry, SonarQube, Kafka)
 * keep identical `en`/`fun` wherever the literal itself is brand-anchored.
 */
export const homeMessages: Messages = {
  // ---- Greeting (greeting.ts + HomePage.tsx) ----
  'home.greeting.night': { en: 'Good night', fun: 'Burning the midnight oil' },
  'home.greeting.morning': { en: 'Good morning', fun: 'Rise and grind' },
  'home.greeting.afternoon': { en: 'Good afternoon', fun: 'Afternoon, champ' },
  'home.greeting.evening': { en: 'Good evening', fun: 'Evening, night owl' },
  'home.greeting.withName': {
    en: '{greeting}, {name}',
    fun: '{greeting}, {name}!',
  },

  // ---- Section rail (HomePage.tsx + SectionRail.tsx) ----
  'home.sections.myWork': { en: 'My Work', fun: 'My Grind' },
  'home.sections.serviceHealth': { en: 'Service Health', fun: 'Vitals Check' },
  'home.sections.catalog': { en: 'Catalog', fun: 'Stash' },
  'home.sectionRail.ariaLabel': { en: 'Home sections', fun: 'Home sections' },

  // ---- Shared nouns composed into subtitles/pills across HomePage.tsx ----
  'home.noun.pullRequests': { en: 'pull requests', fun: 'PRs' },
  'home.noun.assignedIssues': {
    en: 'assigned issues',
    fun: 'issues with your name on them',
  },
  'home.noun.issues': { en: 'issues', fun: 'issues' },
  'home.noun.toPickUp': { en: 'to pick up', fun: 'up for grabs' },
  'home.noun.tickets': { en: 'tickets', fun: 'tickets' },
  'home.noun.alerts': { en: 'alerts', fun: 'red flags' },
  'home.noun.failing': { en: 'failing', fun: 'faceplanting' },
  'home.noun.lagging': { en: 'lagging', fun: 'dragging' },
  'home.noun.entities': { en: 'entities', fun: 'things you own' },
  'home.noun.entity': { en: 'entity', fun: 'thing you own' },
  'home.noun.service': { en: 'service', fun: 'service' },
  'home.noun.services': { en: 'services', fun: 'services' },
  'home.connect.github': {
    en: 'connect github',
    fun: 'connect github, please',
  },
  'home.connect.jira': { en: 'connect jira', fun: 'connect jira, plz' },
  'home.filter.all': { en: 'All', fun: 'Everything' },
  'home.panel.refreshTooltip': {
    en: 'Refresh {label}',
    fun: 'Refresh {label}',
  },

  // ---- My open PRs (HomePage.tsx) ----
  'home.tab.myOpenPrs.title': { en: 'My open PRs', fun: 'My Glorious PRs' },
  'home.tab.myOpenPrs.description': {
    en: 'Pull requests you opened across the Regask org',
    fun: 'PRs you unleashed across the Regask org',
  },
  'home.tab.myOpenPrs.empty': {
    en: 'No open pull requests — all clear.',
    fun: 'Zero open PRs — smooth sailing.',
  },

  // ---- Assigned reviews (HomePage.tsx) ----
  'home.tab.assignedReviews.title': {
    en: 'Assigned reviews',
    fun: 'Reviews On Your Desk',
  },
  'home.tab.assignedReviews.description': {
    en: 'Pull requests waiting on your review',
    fun: 'PRs side-eyeing you for a review',
  },
  'home.tab.assignedReviews.empty': {
    en: 'No reviews waiting on you.',
    fun: 'Nothing waiting on you — go grab a coffee.',
  },

  // ---- Assigned issues (HomePage.tsx) ----
  'home.tab.assignedIssues.title': {
    en: 'Assigned issues',
    fun: 'Issues With Your Name On Them',
  },
  'home.tab.assignedIssues.description': {
    en: 'Issues assigned to you across the Regask org',
    fun: 'Issues that found their way to you',
  },
  'home.tab.assignedIssues.empty': {
    en: 'No issues assigned to you.',
    fun: 'Nothing assigned to you — enjoy it while it lasts.',
  },

  // ---- Tab titles shared between HomePage.tsx's tab bar and each panel ----
  'home.tab.jiraAssigned.title': { en: 'My Jira', fun: 'My Jira' },
  'home.tab.pickUp.title': { en: 'Pick up', fun: 'Grab a Ticket' },
  'home.tab.mergeLordTickets.title': {
    en: 'Merge Lord tickets',
    fun: 'Merge Lord tickets',
  },
  'home.tab.sentryIssues.title': { en: 'Sentry issues', fun: 'Sentry Fires' },
  'home.tab.security.title': { en: 'Security', fun: 'Lock It Down' },
  'home.tab.qualityGates.title': { en: 'Quality gates', fun: 'Gatekeeping' },
  'home.tab.kafkaLag.title': { en: 'Kafka Lag', fun: 'Kafka Traffic Jam' },
  'home.tab.myCatalog.title': { en: 'My catalog', fun: 'My Empire' },

  'home.page.refreshTooltip': {
    en: 'Refreshes Home external data now. Auto refresh: GitHub every 60s, Sentry every 2m, Security and Quality gates every 5m. Polling pauses when this browser tab is hidden.',
    fun: 'Yanks fresh data right now. Auto refresh: GitHub every 60s, Sentry every 2m, Security and Quality gates every 5m. Naps while this tab is hidden.',
  },

  // ---- GithubTabPanel.tsx ----
  'home.github.connectPrompt.prsIssues': {
    en: 'Connect your GitHub account to see your pull requests and issues here.',
    fun: 'Hook up your GitHub account to see your PRs and issues right here.',
  },
  'home.github.connectButton': { en: 'Connect GitHub', fun: 'Connect GitHub' },
  'home.github.loadError': {
    en: 'Could not load from GitHub',
    fun: 'GitHub ghosted us',
  },
  'home.github.retryButton': { en: 'Retry', fun: 'Try again' },

  // ---- GithubTabPanelView.tsx ----
  'home.github.filterAriaLabel': { en: 'Author', fun: 'Author' },
  'home.github.filterExcludeDependabot': {
    en: 'Exclude Dependabot',
    fun: 'No bots, thanks',
  },
  'home.github.onlyDependabotMessage': {
    en: 'No reviews from people — only Dependabot PRs. Switch to All to see them.',
    fun: 'No humans in sight — just Dependabot PRs. Flip to All to see them.',
  },
  'home.github.viewAllOnGithub': {
    en: 'View all {count} on GitHub',
    fun: 'See all {count} over on GitHub',
  },
  'home.github.draftPill': { en: 'Draft', fun: 'WIP' },

  // ---- DependabotTabPanel.tsx (Security tab) ----
  'home.security.connectPrompt': {
    en: 'Connect your GitHub account to see Dependabot alerts here.',
    fun: 'Hook up your GitHub account to see Dependabot alerts right here.',
  },
  'home.security.loadError': {
    en: 'Could not load Dependabot alerts — try again later.',
    fun: 'Dependabot alerts went missing — try again later.',
  },
  'home.security.description': {
    en: 'Open Dependabot alerts across the selected services',
    fun: 'Open red flags across the selected services',
  },
  'home.security.refreshLabel': { en: 'security alerts', fun: 'red flags' },
  'home.security.emptyNoRepo': {
    en: 'None of these services have a GitHub repository configured.',
    fun: 'None of these services even have a GitHub repo hooked up.',
  },
  'home.security.openUnknown': { en: '— open', fun: '— open' },
  'home.security.openCount': { en: '{count} open', fun: '{count} open' },

  // ---- CatalogTabPanel.tsx (My catalog tab) ----
  'home.catalog.loadError': {
    en: 'Could not load the catalog — try again later.',
    fun: 'The catalog is hiding — try again later.',
  },
  'home.catalog.description': {
    en: 'You own {total} {noun}',
    fun: 'You run {total} {noun}',
  },
  'home.catalog.refreshLabel': { en: 'catalog', fun: 'empire' },
  'home.catalog.healthFilterAriaLabel': { en: 'Health', fun: 'Health' },
  'home.catalog.filterFailing': { en: 'Failing', fun: 'Faceplanting' },
  'home.catalog.filterOutOfSync': { en: 'Out of sync', fun: 'Out of step' },
  'home.catalog.emptyFailing': {
    en: 'No failing services — no red deployments right now.',
    fun: 'No faceplants — every deployment is green right now.',
  },
  'home.catalog.emptyOutOfSync': {
    en: 'Nothing out of sync — every deployment is reconciled.',
    fun: 'Nothing out of step — everyone is dancing together.',
  },
  'home.catalog.emptyNone': {
    en: 'No services owned by you yet — ownership comes from CODEOWNERS.',
    fun: 'Nothing in your empire yet — ownership comes from CODEOWNERS.',
  },

  // ---- JiraAssignedTabPanel.tsx (My Jira tab) ----
  'home.jira.connectPromptAssigned': {
    en: 'Connect your Atlassian account to see your Jira issues.',
    fun: 'Hook up your Atlassian account to see your Jira issues.',
  },
  'home.jira.connectButton': { en: 'Connect Jira', fun: 'Connect Jira' },
  'home.jira.loadErrorAssigned': {
    en: 'Could not load Jira issues — try again later.',
    fun: 'Jira issues went awol — try again later.',
  },
  'home.jira.assignedDescription': {
    en: 'Open Jira issues assigned to you',
    fun: 'Jira issues with your name on them',
  },
  'home.jira.emptyAssigned': {
    en: 'No open Jira issues assigned to you.',
    fun: 'Nothing assigned to you — bask in it.',
  },
  'home.jira.emptyFiltered': {
    en: 'No issues match the current filters.',
    fun: 'Nothing matches those filters — try loosening up.',
  },

  // ---- JiraSuggestionsTabPanel.tsx (Pick up tab) ----
  'home.jira.connectPromptSuggestions': {
    en: 'Connect your Atlassian account to get pick-up suggestions.',
    fun: 'Hook up your Atlassian account to get pick-up suggestions.',
  },
  'home.jira.loadErrorSuggestions': {
    en: 'Could not load suggestions — try again later.',
    fun: 'Suggestions went awol — try again later.',
  },
  'home.jira.suggestionsDescription': {
    en: "Unassigned work in the epics you're active in this sprint",
    fun: "Up-for-grabs work in the epics you're already in",
  },
  'home.jira.suggestionsRefreshLabel': {
    en: 'pick-up suggestions',
    fun: 'up-for-grabs tickets',
  },
  'home.jira.emptySuggestions': {
    en: 'Nothing to pick up right now — all claimed.',
    fun: 'Nothing up for grabs — everything is spoken for.',
  },
  'home.jira.assigning': { en: 'Assigning…', fun: 'Calling dibs…' },
  'home.jira.assignToMe': { en: 'Assign to me', fun: 'Dibs' },

  // ---- JiraFilterBar.tsx ----
  'home.jira.filter.status': { en: 'Status', fun: 'Status' },
  'home.jira.filter.statusPlaceholder': {
    en: 'All statuses',
    fun: 'Any status',
  },
  'home.jira.filter.type': { en: 'Type', fun: 'Type' },
  'home.jira.filter.typePlaceholder': { en: 'All types', fun: 'Any type' },
  'home.jira.filter.priority': { en: 'Priority', fun: 'Priority' },
  'home.jira.filter.priorityPlaceholder': {
    en: 'All priorities',
    fun: 'Any priority',
  },
  'home.jira.filter.due': { en: 'Due', fun: 'Due' },
  'home.jira.filter.duePlaceholder': {
    en: 'Any due date',
    fun: 'Whenever',
  },

  // ---- JiraIssueRowItem.tsx ----
  'home.jira.openInJiraAriaLabel': {
    en: 'Open {key} in Jira',
    fun: 'Pop {key} open in Jira',
  },
  'home.jira.openInJiraTitle': { en: 'Open in Jira', fun: 'Open in Jira' },

  // ---- jiraFilters.ts (pure module — due-date buckets) ----
  'home.jira.due.overdueFilter': { en: 'Overdue', fun: 'Way overdue' },
  'home.jira.due.todayFilter': { en: 'Due today', fun: 'Due today' },
  'home.jira.due.tomorrowFilter': {
    en: 'Due in 1 day',
    fun: 'Due tomorrow-ish',
  },
  'home.jira.due.weekFilter': {
    en: 'Due within 7 days',
    fun: 'Due sometime this week',
  },
  'home.jira.due.noneFilter': {
    en: 'No due date',
    fun: 'No deadline, no worries',
  },
  'home.jira.due.overduePill': { en: 'Overdue', fun: 'Yikes, overdue' },
  'home.jira.due.todayPill': { en: 'Due today', fun: 'Due today, chop chop' },
  'home.jira.due.tomorrowPill': {
    en: 'Due tomorrow',
    fun: 'Due tomorrow, no rush',
  },
  'home.jira.due.formattedPill': { en: 'Due {date}', fun: 'Due {date}' },

  // ---- SentryTabPanel.tsx ----
  'home.sentry.loadError': {
    en: 'Could not load Sentry issues — try again later.',
    fun: 'Sentry issues went dark — try again later.',
  },
  'home.sentry.description': {
    en: 'Unresolved · last 14 days · across the selected services',
    fun: 'Still burning · last 14 days · across the selected services',
  },
  'home.sentry.emptyNoProject': {
    en: 'None of these services have a Sentry project configured.',
    fun: 'None of these services even have a Sentry project hooked up.',
  },
  'home.sentry.unresolvedUnknown': { en: '— unresolved', fun: '— unresolved' },
  'home.sentry.unresolvedCount': {
    en: '{count} unresolved',
    fun: '{count} still burning',
  },

  // ---- SonarQubeTabPanel.tsx (Quality gates tab) ----
  'home.quality.loadError': {
    en: 'Could not load SonarQube quality gates — try again later.',
    fun: 'Quality gates went awol — try again later.',
  },
  'home.quality.description': {
    en: 'SonarQube quality gates across the selected services',
    fun: 'Gatekeeping across the selected services',
  },
  'home.quality.refreshLabel': { en: 'quality gates', fun: 'gatekeeping' },
  'home.quality.emptyNoProject': {
    en: 'None of these services have a SonarQube project configured.',
    fun: 'None of these services even have a SonarQube project hooked up.',
  },
  'home.quality.gateUnknown': { en: '— gate', fun: '— gate' },
  'home.quality.gateFailed': { en: 'Gate failed', fun: 'Gate slammed shut' },
  'home.quality.gatePassed': { en: 'Gate passed', fun: 'Gate wide open' },
  'home.quality.metric.bugs': { en: 'bugs', fun: 'critters' },
  'home.quality.metric.vulns': { en: 'vulns', fun: 'vulns' },
  'home.quality.metric.smells': { en: 'smells', fun: 'whiffs' },

  // ---- KafkaLagTabPanel.tsx / KafkaLagTabPanelView.tsx ----
  'home.kafka.loadError': {
    en: 'Could not load Kafka consumer lag — try again later.',
    fun: 'Kafka lag data went awol — try again later.',
  },
  'home.kafka.description': {
    en: 'Consumer-group lag across the Kafka topics the selected services consume',
    fun: 'Who is dragging their feet across the selected Kafka topics',
  },
  'home.kafka.emptyTitle': {
    en: 'No Kafka lag',
    fun: 'Nobody dragging their feet',
  },
  'home.kafka.emptyDescription': {
    en: 'None of these services are part of a Kafka consumer group yet, or their lag data hasn’t been collected. Use Refresh to re-check.',
    fun: 'None of these services are in a Kafka consumer group yet, or the lag data hasn’t landed. Hit Refresh to re-check.',
  },

  // ---- MergeLordTicketsView.tsx (brand: identical en/fun) ----
  'home.mergeLord.refreshTooltip': {
    en: 'Refresh Merge Lord tickets',
    fun: 'Refresh Merge Lord tickets',
  },
  'home.mergeLord.loading': { en: 'Loading…', fun: 'Rolling the dice…' },
  'home.mergeLord.loadError': {
    en: 'Could not load the Merge Lord board.',
    fun: 'Could not load the Merge Lord board.',
  },
  'home.mergeLord.description': {
    en: 'Stale PRs on the Merge Lord board (label: merge-lord-on-mission).',
    fun: 'Stale PRs on the Merge Lord board (label: merge-lord-on-mission).',
  },
  'home.mergeLord.empty': {
    en: 'No Merge Lord tickets right now.',
    fun: 'No Merge Lord tickets right now.',
  },

  // ---- OwnerFilter.tsx ----
  'home.ownerFilter.label': { en: 'Owner', fun: 'Owner' },
  'home.ownerFilter.allOwners': { en: 'All owners', fun: 'Everybody' },
  // The Catalog section's catalog is already limited to the user's services, so
  // its clear option cannot claim "all owners" the way Service Health can.
  'home.ownerFilter.allMine': {
    en: 'Everything I own',
    fun: 'My Whole Empire',
  },

  // ---- CharacterBadge.tsx ----
  'home.characterBadge.ariaLabel': {
    en: '{title} — hover or focus for details',
    fun: '{title} — poke it for the details',
  },
};
